import type {LocalDate} from '../domain/local-date.value-object';
import type {ScheduleCalendarRange} from '../domain/schedule-calendar';
import type {Weekday} from '../domain/weekday.value-object';

const LOCAL_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const DAYS_IN_WEEK = 7;
/** Six Monday-first weeks: enough rows for any month, so mini-months keep one height. */
const MINI_MONTH_GRID_DAYS = 42;
const MONTHS_IN_YEAR = 12;
const LAST_DAY_OF_DECEMBER = 31;
const MIN_MONTH = 1;
const MAX_MONTH = 12;
const MIN_DAY = 1;
const MONDAY = 1;

const DAYS_IN_MONTH: Readonly<Record<number, number>> = {
  1: 31,
  2: 28,
  3: 31,
  4: 30,
  5: 31,
  6: 30,
  7: 31,
  8: 31,
  9: 30,
  10: 31,
  11: 30,
  12: 31,
} as const;

/** `getUTCDay()` → ISO weekday: Sunday (0) is 7. */
const WEEKDAY_BY_UTC_DAY: Readonly<Record<number, Weekday>> = {
  0: 7,
  1: 1,
  2: 2,
  3: 3,
  4: 4,
  5: 5,
  6: 6,
} as const;

const pad2 = (value: number): string => String(value).padStart(2, '0');

const isLeapYear = (year: number): boolean =>
  (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

const daysInMonth = (year: number, month: number): number => {
  if (month === 2 && isLeapYear(year)) {
    return 29;
  }

  return DAYS_IN_MONTH[month] ?? 0;
};

const localDateOrThrow = (year: number, month: number, day: number): LocalDate => {
  const value = `${year}-${pad2(month)}-${pad2(day)}`;
  if (!isLocalDate(value)) {
    throw new Error(`Invalid calendar date: ${value}`);
  }

  return value;
};

const partsOf = (date: LocalDate): {year: number; month: number; day: number} => {
  const [year, month, day] = date.split('-').map(Number);
  return {year: year ?? 0, month: month ?? MIN_MONTH, day: day ?? MIN_DAY};
};

/** Type guard for the {@link LocalDate} brand; rejects impossible dates like `2026-13-40`. */
export const isLocalDate = (value: string): value is LocalDate => {
  const match = LOCAL_DATE_PATTERN.exec(value);

  if (match === null) {
    return false;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  return (
    month >= MIN_MONTH && month <= MAX_MONTH && day >= MIN_DAY && day <= daysInMonth(year, month)
  );
};

const utcMs = (date: LocalDate): number => Date.parse(`${date}T00:00:00.000Z`);

const localDateOfInstant = (instantMs: number): LocalDate => {
  const date = new Date(instantMs);
  return localDateOrThrow(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
};

/**
 * Add a signed number of calendar days. The same `YYYY-MM-DD` UTC arithmetic the
 * server uses, so the shifted date never depends on a timezone or on DST.
 */
export const addDays = (date: LocalDate, days: number): LocalDate =>
  localDateOfInstant(utcMs(date) + days * MS_PER_DAY);

/** Inclusive day count of a range, the same count the server caps at 366. */
export const inclusiveDayCount = (range: ScheduleCalendarRange): number =>
  Math.floor((utcMs(range.to) - utcMs(range.from)) / MS_PER_DAY) + 1;

/** ISO weekday of a calendar date: Monday = 1 … Sunday = 7. */
export const weekdayOf = (date: LocalDate): Weekday =>
  WEEKDAY_BY_UTC_DAY[new Date(utcMs(date)).getUTCDay()] ?? MONDAY;

/** Monday of the week containing `date`. */
export const mondayOnOrBefore = (date: LocalDate): LocalDate =>
  addDays(date, MONDAY - weekdayOf(date));

/** Monday through Sunday of the week containing `date`. */
export const weekRange = (date: LocalDate): ScheduleCalendarRange => {
  const from = mondayOnOrBefore(date);
  return {from, to: addDays(from, DAYS_IN_WEEK - 1)};
};

/**
 * Monday-first grid of the month containing `date`: starts on the Monday on or
 * before the 1st and ends on the Sunday on or after the last day (35–42 days).
 */
export const monthGridRange = (date: LocalDate): ScheduleCalendarRange => {
  const {year, month} = partsOf(date);
  const first = localDateOrThrow(year, month, MIN_DAY);
  const last = localDateOrThrow(year, month, daysInMonth(year, month));

  return {from: mondayOnOrBefore(first), to: addDays(mondayOnOrBefore(last), DAYS_IN_WEEK - 1)};
};

/** Shift a date by whole months, keeping the day where the target month has it. */
export const addMonths = (date: LocalDate, months: number): LocalDate => {
  const {year, month, day} = partsOf(date);
  const zeroBasedMonth = month - MIN_MONTH + months;
  const shiftedYears = Math.floor(zeroBasedMonth / MONTHS_IN_YEAR);
  const shiftedMonth = zeroBasedMonth - shiftedYears * MONTHS_IN_YEAR + MIN_MONTH;
  const shiftedYear = year + shiftedYears;

  return localDateOrThrow(
    shiftedYear,
    shiftedMonth,
    Math.min(day, daysInMonth(shiftedYear, shiftedMonth)),
  );
};

/** Shift a date by whole years; Feb 29 lands on Feb 28 in a common year. */
export const addYears = (date: LocalDate, years: number): LocalDate =>
  addMonths(date, years * MONTHS_IN_YEAR);

/** The 1st of the month containing `date`. */
export const startOfMonth = (date: LocalDate): LocalDate => {
  const {year, month} = partsOf(date);
  return localDateOrThrow(year, month, MIN_DAY);
};

/** Whether two dates fall in the same calendar month. */
export const isSameMonth = (date: LocalDate, other: LocalDate): boolean =>
  startOfMonth(date) === startOfMonth(other);

/** January 1 through December 31 of the year containing `date` (365 or 366 days). */
export const yearRange = (date: LocalDate): ScheduleCalendarRange => {
  const {year} = partsOf(date);
  return {
    from: localDateOrThrow(year, MIN_MONTH, MIN_DAY),
    to: localDateOrThrow(year, MAX_MONTH, LAST_DAY_OF_DECEMBER),
  };
};

/** The 1st of every month in the year containing `date`, January first. */
export const yearMonthStarts = (date: LocalDate): readonly LocalDate[] => {
  const {year} = partsOf(date);
  return Array.from({length: MONTHS_IN_YEAR}, (_, index) =>
    localDateOrThrow(year, MIN_MONTH + index, MIN_DAY),
  );
};

/** A fixed six-week grid from the Monday on or before the 1st of the month containing `date`. */
export const miniMonthGridRange = (date: LocalDate): ScheduleCalendarRange => {
  const from = mondayOnOrBefore(startOfMonth(date));
  return {from, to: addDays(from, MINI_MONTH_GRID_DAYS - 1)};
};

/** Every date of an inclusive range, in order. */
export const datesInRange = (range: ScheduleCalendarRange): readonly LocalDate[] =>
  Array.from({length: inclusiveDayCount(range)}, (_, index) => addDays(range.from, index));

/** Parse `YYYY-MM-DD` into a browser-local `Date` at noon, stable for the `DatePicker`. */
export const parseLocalDateString = (value: LocalDate): Date => {
  const {year, month, day} = partsOf(value);
  return new Date(year, month - 1, day, 12, 0, 0, 0);
};

/** Format a picked `Date` back into the browser-local calendar date. */
export const toLocalDateString = (date: Date): LocalDate =>
  localDateOrThrow(date.getFullYear(), date.getMonth() + 1, date.getDate());
